import React, { useMemo, useState } from "react";
import { Box, ButtonBase, Popover } from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";

/*
 * ThemedTimePicker — drop-in replacement for <input type="time">.
 * Value in/out is the same "HH:mm" (24h) string, and onChange receives
 * { target: { name, value } }, so existing handlers work as-is.
 * `step` is in seconds, like the native input (300 = 5-minute slots).
 */

const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";
const ERROR = "#C42B24";

const pad = (n) => String(n).padStart(2, "0");
const to12 = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  return `${pad(((h + 11) % 12) + 1)}:${pad(m)} ${ap}`;
};

const colSx = {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  maxHeight: 216,
  overflowY: "auto",
  pr: 0.5,
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": { background: "#D5DEE7", borderRadius: 6 },
};

const ThemedTimePicker = ({
  name,
  id,
  value,
  onChange,
  error = false,
  step = 300,
  disabled = false,
  height = 40,
  width,
  placeholder = "--:--",
  sx,
}) => {
  const [anchor, setAnchor] = useState(null);
  const minuteStep = Math.max(1, Math.round((step || 60) / 60));
  const minutes = useMemo(() => {
    const out = [];
    for (let m = 0; m < 60; m += minuteStep) out.push(m);
    return out;
  }, [minuteStep]);
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);

  const [h, m] = value ? value.split(":").map(Number) : [null, null];
  const emit = (v) => onChange && onChange({ target: { name, value: v, id } });
  const setH = (hh) => emit(`${pad(hh)}:${pad(m ?? 0)}`);
  const setM = (mm) => emit(`${pad(h ?? 9)}:${pad(mm)}`);

  const cell = (on) => ({
    height: 32,
    minWidth: 52,
    borderRadius: "8px",
    fontFamily: MONO,
    fontSize: 13,
    fontWeight: on ? 700 : 500,
    color: on ? "#FFFFFF" : INK,
    background: on ? PRIMARY : "transparent",
    flexShrink: 0,
    "&:hover": { background: on ? "#084767" : "#EAF2FB" },
  });

  return (
    <>
      <ButtonBase
        id={id}
        onClick={(e) => !disabled && setAnchor(e.currentTarget)}
        disabled={disabled}
        disableRipple
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          width: width || 150,
          height,
          px: 1.5,
          boxSizing: "border-box",
          background: disabled ? "#F4F7FA" : "#FFFFFF",
          border: `1px solid ${error ? ERROR : anchor ? PRIMARY : FIELD_EDGE}`,
          borderRadius: "10px",
          fontFamily: MONO,
          fontSize: 13.5,
          color: value ? INK : "#8A97A8",
          "&:hover": { borderColor: error ? ERROR : anchor ? PRIMARY : "#B4C6DA" },
          ...sx,
        }}
      >
        <span>{to12(value) || placeholder}</span>
        <AccessTimeRoundedIcon sx={{ fontSize: 17, color: anchor ? PRIMARY : MUTED }} />
      </ButtonBase>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              p: 1.25,
              borderRadius: "14px",
              border: `1px solid ${LINE}`,
              boxShadow: "0 16px 36px -12px rgba(15,27,42,0.28)",
              fontFamily: FONT,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box>
            <Box sx={{ px: 0.5, pb: 0.75, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED }}>Hour</Box>
            <Box sx={colSx}>
              {hours.map((hh) => (
                <ButtonBase key={hh} onClick={() => setH(hh)} sx={cell(hh === h)}>{pad(hh)}</ButtonBase>
              ))}
            </Box>
          </Box>
          <Box sx={{ width: "1px", background: "#EDF1F5" }} />
          <Box>
            <Box sx={{ px: 0.5, pb: 0.75, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED }}>Min</Box>
            <Box sx={colSx}>
              {minutes.map((mm) => (
                <ButtonBase key={mm} onClick={() => setM(mm)} sx={cell(mm === m)}>{pad(mm)}</ButtonBase>
              ))}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, pt: 1, borderTop: "1px solid #EDF1F5" }}>
          <Box sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 600, color: MUTED, pl: 0.5 }}>{to12(value) || "—"}</Box>
          <ButtonBase
            onClick={() => setAnchor(null)}
            sx={{ height: 30, px: 1.5, borderRadius: "8px", fontFamily: FONT, fontSize: 13, fontWeight: 700, color: "#FFFFFF", background: PRIMARY, "&:hover": { background: "#084767" } }}
          >
            Done
          </ButtonBase>
        </Box>
      </Popover>
    </>
  );
};

export default ThemedTimePicker;
